'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import styles from './LandingDetails.module.css'

const content = {
  create: {
    introTitle: 'landingV2_createIntroTitle',
    intro: 'landingV2_createIntroBody',
    stepsTitle: 'landingV2_createStepsTitle',
    steps: [
      {
        title: 'landingV2_createStep1Title',
        text: 'landingV2_createStep1Body',
      },
      {
        title: 'landingV2_createStep2Title',
        text: 'landingV2_createStep2Body',
      },
      {
        title: 'landingV2_createStep3Title',
        text: 'landingV2_createStep3Body',
      },
    ],
    detailTitle: 'landingV2_createDetailTitle',
    detail: 'landingV2_createDetailBody',
    extra: 'landingV2_createDetailExtra',
    example: [
      { label: 'landingV2_subject', text: 'landingV2_subjectExample' },
      { label: 'landingV2_setting', text: 'landingV2_settingExample' },
      { label: 'landingV2_mood', text: 'landingV2_moodExample' },
    ],
    links: [
      { href: '/articles/prompting-guide', label: 'landingV2_promptGuide' },
      {
        href: '/articles/promptchan-video-tutorial',
        label: 'landingV2_firstVideo',
      },
    ],
  },
  chat: {
    introTitle: 'landingV2_chatIntroTitle',
    intro: 'landingV2_chatIntroBody',
    stepsTitle: 'landingV2_chatStepsTitle',
    steps: [
      { title: 'landingV2_chatStep1Title', text: 'landingV2_chatStep1Body' },
      { title: 'landingV2_chatStep2Title', text: 'landingV2_chatStep2Body' },
      { title: 'landingV2_chatStep3Title', text: 'landingV2_chatStep3Body' },
    ],
    detailTitle: 'landingV2_chatDetailTitle',
    detail: 'landingV2_chatDetailBody',
    example: [
      { label: 'landingV2_personality', text: 'landingV2_personalityExample' },
      { label: 'landingV2_interests', text: 'landingV2_interestsExample' },
      {
        label: 'landingV2_openingScene',
        text: 'landingV2_openingSceneExample',
      },
    ],
    links: [
      {
        href: '/articles/promptchan-chat-tutorial',
        label: 'landingV2_chatGuide',
      },
      { href: '/chat', label: 'landingV2_discoverCharacters', action: true },
    ],
  },
}

export default function LandingDetails({
  focus = 'create',
  placement = 'details',
  showCompanions = true,
  showSteps = true,
}) {
  const { t } = useTranslation()
  const copy = content[focus === 'chat' ? 'chat' : 'create']

  if (placement === 'intro') {
    return (
      <section
        className={styles.intro}
        aria-labelledby="landing-intro-heading"
        data-landing-section="details"
      >
        <h2 id="landing-intro-heading">{t(copy.introTitle)}</h2>
        <p>{t(copy.intro)}</p>
      </section>
    )
  }

  return (
    <div className={styles.details} data-landing-section="details">
      {showSteps && (
        <section
          className={styles.stepsSection}
          aria-labelledby="landing-steps-heading"
        >
          <div className={styles.stepsHeading}>
            <h2 id="landing-steps-heading">{t(copy.stepsTitle)}</h2>
          </div>
          <ol className={styles.steps}>
            {copy.steps.map(({ title, text }, index) => (
              <li key={title}>
                <span className={styles.stepNumber} aria-hidden="true">
                  0{index + 1}
                </span>
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section
        className={styles.direction}
        aria-labelledby="landing-direction-heading"
      >
        <div className={styles.directionCopy}>
          <h2 id="landing-direction-heading">{t(copy.detailTitle)}</h2>
          <p>{t(copy.detail)}</p>
          {copy.extra && <p>{t(copy.extra)}</p>}
          <div className={styles.guideLinks}>
            {copy.links.map(({ href, label, action }) => (
              <Link
                key={href}
                href={href}
                className={action ? styles.actionLink : styles.guideLink}
              >
                {t(label)}
                <span aria-hidden="true">↗</span>
              </Link>
            ))}
          </div>
        </div>
        <div className={styles.example}>
          <dl>
            {copy.example.map(({ label, text }) => (
              <div key={label}>
                <dt>{t(label)}</dt>
                <dd>{t(text)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
      {showCompanions && (
        <aside
          className={styles.companions}
          aria-labelledby="landing-companions-heading"
        >
          <h3 id="landing-companions-heading">
            {t('landingV2_notJustGirlfriends')}
          </h3>
          <p>{t('landingV2_companionsBody')}</p>
          <Link
            href="/chat"
            className={styles.actionLink}
            data-landing-action="discover"
          >
            {t('landingV2_discoverCompanions')}{' '}
            <span aria-hidden="true">↗</span>
          </Link>
        </aside>
      )}
    </div>
  )
}
